import { DocumentNode, gql, useQuery } from '@apollo/client';
import {
  Button,
  Center,
  Flex,
  Heading,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { Post } from '@/components/Post';
import {
  PostingExperience,
  usePostingExperience,
} from '@/components/PostingExperience';
import { useOrganizationPermissions } from '@/hooks/useOrganizationPermissions';
import { useSignUpPrompt } from '@/hooks/useSignUpPrompt';
import { Card } from '@/imports/ui/chakra/components/Card';

import { RecentActivityCard_OrganizationFragment } from './graphql-types/RecentActivityCard_OrganizationFragment';
import {
  RecentActivityCard_OrganizationTierQuery,
  RecentActivityCard_OrganizationTierQueryVariables,
} from './graphql-types/RecentActivityCard_OrganizationTierQuery';

const ORGANIZATION_FRAGMENT = gql`
  fragment RecentActivityCard_OrganizationFragment on Organization {
    _id
    name
    recentActivityV2(input: {}) {
      results {
        ...Post_PostV2Fragment
      }
      hasMore
    }
  }
  ${Post.postFragment}
`;

const ORGANIZATION_TIER_QUERY = gql`
  query RecentActivityCard_OrganizationTierQuery(
    $input: SingleOrganizationInput!
  ) {
    organization(input: $input) {
      result {
        _id
        sponsorship {
          currentTier {
            canMemberPostOnDiscussionBoard
          }
        }
      }
    }
  }
`;

const StartADiscussionButton: React.FC<{
  organizationId: string;
  onClick: () => void;
}> = ({ organizationId, onClick }) => {
  const { promptModal, withPrompt, loading: promptLoading } = useSignUpPrompt();
  const organizationPermissions = useOrganizationPermissions(organizationId);

  if (
    organizationPermissions.isLoading ||
    !organizationPermissions.canCreatePostInOrganization ||
    promptLoading
  ) {
    return null;
  }

  return (
    <>
      {promptModal}
      <Button onClick={withPrompt(onClick)}>Start a Discussion</Button>
    </>
  );
};

export const RecentActivityCard: React.FC<{
  organization: RecentActivityCard_OrganizationFragment;
  recentActivityRef?: React.RefObject<HTMLDivElement>;
}> & { organizationFragment: DocumentNode } = ({
  organization,
  recentActivityRef,
}) => {
  const postingExperience = usePostingExperience();
  const hasRecentActivity = !!organization?.recentActivityV2?.results?.length;
  const { data: tierData } = useQuery<
    RecentActivityCard_OrganizationTierQuery,
    RecentActivityCard_OrganizationTierQueryVariables
  >(ORGANIZATION_TIER_QUERY, {
    variables: { input: { id: organization._id } },
  });
  const canMemberPostOnDiscussionBoard =
    tierData?.organization?.result?.sponsorship?.currentTier
      ?.canMemberPostOnDiscussionBoard;

  return (
    <Card w="full" px="4" py="6">
      <Flex mb="4" justify="space-between" ref={recentActivityRef}>
        <Heading size="md">Recent Activity</Heading>
        {canMemberPostOnDiscussionBoard ? (
          <StartADiscussionButton
            organizationId={organization._id}
            onClick={postingExperience.createPost}
          />
        ) : null}
        <PostingExperience
          {...postingExperience}
          options={{
            organization,
          }}
        />
      </Flex>

      {hasRecentActivity ? (
        <>
          <VStack spacing="4">
            {organization.recentActivityV2.results.map((post) => {
              return <Post post={post} key={post._id} />;
            })}

            {organization.recentActivityV2.hasMore && (
              <Center>
                {/* TODO: uhhh, what is this lol */}
                <Link as={NextLink} href="./discussions" pt="2">
                  See All Discussions
                </Link>
              </Center>
            )}
          </VStack>
        </>
      ) : (
        <Flex
          color="gray.600"
          h="264px"
          align="center"
          justify="center"
          bg="gray.50"
          borderRadius="base"
        >
          <Text>There are no discussions yet</Text>
        </Flex>
      )}
    </Card>
  );
};

RecentActivityCard.organizationFragment = ORGANIZATION_FRAGMENT;
